<template>
    <modal
        name="dd-2fa-modal"
        transition="nice-modal-fade"
        classes="dd-2fa-modal-class"
        :min-width="360"
        :min-height="360"
        :adaptive="true"
        :scrollable="true"
        width="520px"
        height="auto"
        :reset="false"
        :click-to-close="false"
        @before-open="beforeOpen"
        @before-close="beforeClose">

        <div class="dd-2fa-content">
            <button class="dd-2fa-close-btn" @click="closeModal">
                <i class="fa fa-times"></i>
            </button>

            <h2>
                <i class="fa fa-mobile"></i> {{ title[getCurrentLocale] }}
            </h2>
            <p class="dd-2fa-subtitle">{{ subtitle[getCurrentLocale] }}</p>

            <p class="dd-2fa-helper dd-2fa-helper--error" v-if="pinError">
                <i class="fa fa-exclamation-circle"></i> {{ pinError }}
            </p>

            <div class="dd-2fa-step-card" :class="{
                'dd-2fa-step-card--active': !pinSent,
                'dd-2fa-step-card--done': pinSent
            }">
                <div class="dd-2fa-step-card-header">
                    <div class="dd-2fa-step-title">
                        <span class="dd-2fa-step-number">
                            <i class="fa fa-check" v-if="pinSent"></i>
                            <template v-else>1</template>
                        </span>
                        {{ stepSendTitle[getCurrentLocale] }}
                        <span class="dd-2fa-step-badge dd-2fa-step-badge--active" v-if="!pinSent">
                            {{ stepActiveLabel[getCurrentLocale] }}
                        </span>
                        <span class="dd-2fa-step-badge dd-2fa-step-badge--done" v-else>
                            {{ stepDoneLabel[getCurrentLocale] }}
                        </span>
                    </div>
                    <span class="dd-2fa-step-card__required">{{ stepRequiredLabel[getCurrentLocale] }}</span>
                </div>
                <div class="form-group">
                    <label>{{ phoneLabel[getCurrentLocale] }}</label>
                    <input
                        type="tel"
                        class="input-text full-width dd-2fa-phone-input"
                        :value="phoneValue"
                        @input="onPhoneInput"
                        inputmode="tel"
                        autocomplete="tel"
                        :placeholder="phonePlaceholder[getCurrentLocale]" />
                    <p class="dd-2fa-phone-hint">{{ phoneHint[getCurrentLocale] }}</p>
                </div>

                <div class="dd-2fa-send-row">
                    <button
                        class="button btn-medium dd-2fa-send-btn"
                        :disabled="isSending || resendCooldownSeconds > 0"
                        @click.prevent="sendSmsCode">
                        <template v-if="resendCooldownSeconds > 0">
                            {{ resendCooldownLabel[getCurrentLocale].replace('{seconds}', resendCooldownSeconds) }}
                        </template>
                        <template v-else>
                            {{ pinSent ? resendCode[getCurrentLocale] : sendCode[getCurrentLocale] }}
                        </template>
                    </button>
                    <span class="dd-2fa-status" v-if="pinSent && resendCooldownSeconds === 0">
                        <i class="fa fa-check-circle"></i> {{ codeSent[getCurrentLocale] }}
                    </span>
                </div>
            </div>

            <div class="dd-2fa-step-card" :class="{
                'dd-2fa-step-card--active': pinSent,
                'dd-2fa-step-card--disabled': !pinSent
            }">
                <div class="dd-2fa-step-card-header">
                    <div class="dd-2fa-step-title">
                        <span class="dd-2fa-step-number">2</span>
                        {{ stepPinTitle[getCurrentLocale] }}
                        <span class="dd-2fa-step-badge dd-2fa-step-badge--locked" v-if="!pinSent">
                            <i class="fa fa-lock"></i> {{ stepLockedLabel[getCurrentLocale] }}
                        </span>
                    </div>
                    <span class="dd-2fa-step-card__required">{{ stepRequiredLabel[getCurrentLocale] }}</span>
                </div>
                <div class="form-group dd-2fa-pin-group">
                    <label>{{ pinLabel[getCurrentLocale] }}</label>
                    <pin-code-input
                        ref="pinInput"
                        v-model="pinValue"
                        :disabled="!pinSent"
                        :digit-label="pinDigitLabel[getCurrentLocale]" />
                </div>
                <p class="dd-2fa-helper" v-if="!pinSent">
                    <i class="fa fa-info-circle"></i> {{ stepPinHelper[getCurrentLocale] }}
                </p>
                <p class="dd-2fa-helper" v-if="pinSent && !pinComplete">
                    {{ stepPinFormatHint[getCurrentLocale] }}
                </p>
                <label class="dd-2fa-pin-confirm" v-if="pinSent && pinComplete">
                    <input type="checkbox" v-model="pinConfirmed" />
                    <span>{{ pinConfirmLabel[getCurrentLocale] }}</span>
                </label>
            </div>

            <div class="dd-2fa-actions">
                <a class="dd-2fa-cancel-btn uppercase" @click.prevent="closeModal">
                    {{ cancel[getCurrentLocale] }}
                </a>
                <button
                    class="button btn-medium uppercase dd-2fa-confirm-btn"
                    :disabled="!canConfirmPin"
                    @click.prevent="confirmPin">
                    {{ confirm[getCurrentLocale] }}
                </button>
            </div>
        </div>
    </modal>
</template>


<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import PinCodeInput from './PinCodeInput.vue';
import {
    formatGermanPhoneDisplay,
    isValidGermanPhone,
    toGermanPhoneApi,
} from '../../../utils/phone.js';

export default {
    components: {
        'pin-code-input': PinCodeInput,
    },

    data: function () {
        return {
            modulo: 'booking',
            rebookingMode: 'rebooking',
            servicesMode: null,
            bookingKeyCode: null,
            bookingIdentifier: null,
            pinError: '',
            phoneValue: '',
            pinValue: '',
            pinConfirmed: false,
            pinSent: false,
            isSending: false,
            resendCooldownSeconds: 0,
            resendTimerId: null,
            title: {
                en: 'SMS verification for direct debit',
                de: 'SMS-Verifizierung fuer Lastschrift',
            },
            subtitle: {
                en: 'Please request an SMS code and enter the 6-digit PIN to continue.',
                de: 'Bitte fordern Sie einen SMS-Code an und geben Sie den 6-stelligen PIN ein.',
            },
            phoneLabel: {
                en: 'Phone number',
                de: 'Telefonnummer',
            },
            phonePlaceholder: {
                en: 'e.g. +49 171 1234567',
                de: 'z.B. +49 171 1234567',
            },
            phoneHint: {
                en: 'German numbers only (+49). Spaces are added automatically.',
                de: 'Nur deutsche Nummern (+49). Leerzeichen werden automatisch gesetzt.',
            },
            stepSendTitle: {
                en: 'Send SMS code',
                de: 'SMS-Code senden',
            },
            stepPinTitle: {
                en: 'Enter SMS PIN',
                de: 'SMS-PIN eingeben',
            },
            stepPinHelper: {
                en: 'Complete step 1 first — then enter the PIN here.',
                de: 'Zuerst Schritt 1 abschliessen — danach PIN hier eingeben.',
            },
            stepActiveLabel: {
                en: 'Current step',
                de: 'Aktueller Schritt',
            },
            stepRequiredLabel: {
                en: '* required',
                de: '* Pflicht',
            },
            stepDoneLabel: {
                en: 'Done',
                de: 'Erledigt',
            },
            stepLockedLabel: {
                en: 'Locked',
                de: 'Gesperrt',
            },
            stepPinFormatHint: {
                en: 'Enter the 6-digit PIN from your SMS.',
                de: 'Bitte den 6-stelligen PIN aus der SMS eingeben.',
            },
            pinConfirmLabel: {
                en: 'The PIN was entered correctly.',
                de: 'Die PIN wurde richtig eingegeben.',
            },
            pinLabel: {
                en: 'SMS PIN',
                de: 'SMS-PIN',
            },
            pinDigitLabel: {
                en: 'PIN digit',
                de: 'PIN-Ziffer',
            },
            sendCode: {
                en: 'Send SMS code',
                de: 'SMS-Code senden',
            },
            resendCode: {
                en: 'Send again',
                de: 'Erneut senden',
            },
            resendCooldownLabel: {
                en: 'Send again in {seconds}s',
                de: 'Erneut senden in {seconds}s',
            },
            codeSent: {
                en: 'Code sent',
                de: 'Code gesendet',
            },
            confirm: {
                en: 'Enter PIN',
                de: 'PIN eingeben',
            },
            cancel: {
                en: 'Cancel',
                de: 'Abbrechen',
            },
            msgNeedPhone: {
                en: 'Please enter a valid German phone number with country code +49.',
                de: 'Bitte eine gueltige deutsche Telefonnummer mit Laendervorwahl +49 eingeben.',
            },
            msgNeedPin: {
                en: 'Please enter the 6-digit SMS PIN.',
                de: 'Bitte den 6-stelligen SMS-PIN eingeben.',
            },
            msgMissingBookingKey: {
                en: '2FA cannot be started because booking key is missing.',
                de: '2FA kann nicht gestartet werden, da der Buchungsschluessel fehlt.',
            },
            msgMissingBookingIdentifier: {
                en: '2FA cannot be started because booking data is missing. Please log in with your booking number first.',
                de: '2FA kann nicht gestartet werden, da Buchungsdaten fehlen. Bitte zuerst mit Buchungsnummer anmelden.',
            },
            msgServicesBookingnoNotSupported: {
                en: 'SMS verification for direct debit is not available in this services mode yet.',
                de: 'SMS-Verifizierung fuer Lastschrift ist in diesem Services-Modus noch nicht verfuegbar.',
            },
        };
    },

    computed: {
        ...mapGetters([
            'getCurrentLocale',
        ]),

        pinComplete: function () {
            return /^[0-9]{6}$/.test(this.pinValue);
        },

        canConfirmPin: function () {
            return this.pinSent === true
                && this.pinComplete === true
                && this.pinConfirmed === true;
        },
    },

    watch: {
        pinValue: function () {
            this.pinConfirmed = false;
        },

        pinError: function (value) {
            if (value) {
                this.pinValue = '';
                this.pinConfirmed = false;
                this.focusPinInput();
            }
        },
    },

    beforeDestroy: function () {
        this.clearResendCooldown();
    },

    methods: {
        beforeOpen: function (event) {
            const params = event.params || {};

            this.modulo = params.modulo || 'booking';
            this.rebookingMode = params.rebookingMode || 'rebooking';
            this.servicesMode = params.servicesMode || null;
            this.bookingKeyCode = params.bookingKeyCode || null;
            this.bookingIdentifier = params.bookingIdentifier || null;
            this.pinError = params.pinError || '';
            this.phoneValue = params.phone ? formatGermanPhoneDisplay(params.phone) : '';
            this.pinValue = '';
            this.pinConfirmed = false;
            this.pinSent = false;
            this.isSending = false;
            this.clearResendCooldown();
        },

        beforeClose: function () {
            this.modulo = 'booking';
            this.rebookingMode = 'rebooking';
            this.servicesMode = null;
            this.bookingKeyCode = null;
            this.bookingIdentifier = null;
            this.pinError = '';
            this.phoneValue = '';
            this.pinValue = '';
            this.pinConfirmed = false;
            this.pinSent = false;
            this.isSending = false;
            this.clearResendCooldown();
        },

        clearResendCooldown: function () {
            if (this.resendTimerId) {
                clearInterval(this.resendTimerId);
                this.resendTimerId = null;
            }
            this.resendCooldownSeconds = 0;
        },

        startResendCooldown: function () {
            this.clearResendCooldown();
            this.resendCooldownSeconds = 60;
            this.resendTimerId = setInterval(() => {
                if (this.resendCooldownSeconds <= 1) {
                    this.clearResendCooldown();
                    return;
                }
                this.resendCooldownSeconds -= 1;
            }, 1000);
        },

        onPhoneInput: function (event) {
            this.phoneValue = formatGermanPhoneDisplay(event.target.value);

            this.$nextTick(() => {
                const input = event.target;
                if (input) {
                    input.setSelectionRange(this.phoneValue.length, this.phoneValue.length);
                }
            });
        },

        focusPinInput: function () {
            this.$nextTick(() => {
                if (this.$refs.pinInput) {
                    this.$refs.pinInput.focusFirst();
                }
            });
        },

        closeModal: function () {
            this.$modal.hide('dd-2fa-modal');
        },

        sendSmsCode: function () {
            let payload = null;
            let endpoint = '';

            if (!isValidGermanPhone(this.phoneValue)) {
                this.$toasted.global.sdr_app_error({
                    message: this.msgNeedPhone[this.getCurrentLocale],
                });
                return;
            }

            const mobile = toGermanPhoneApi(this.phoneValue);

            if (this.modulo === 'rebooking' && this.rebookingMode === 'namechange') {
                if (!this.bookingIdentifier) {
                    this.$toasted.global.sdr_app_error({
                        message: this.msgMissingBookingIdentifier[this.getCurrentLocale],
                    });
                    return;
                }
                endpoint = '/namechange/2fa/send';
                payload = {
                    bookingIdentifier: this.bookingIdentifier,
                    mobile: mobile,
                };
            } else if (this.modulo === 'services' && this.servicesMode === 'registration') {
                if (!this.bookingKeyCode) {
                    this.$toasted.global.sdr_app_error({
                        message: this.msgMissingBookingKey[this.getCurrentLocale],
                    });
                    return;
                }
                endpoint = '/registration/2fa/send';
                payload = {
                    bookingKeyCode: this.bookingKeyCode,
                    mobile: mobile,
                };
            } else if (this.modulo === 'services' && this.servicesMode === 'bookingno') {
                this.$toasted.global.sdr_app_error({
                    message: this.msgServicesBookingnoNotSupported[this.getCurrentLocale],
                });
                return;
            } else {
                if (!this.bookingKeyCode) {
                    this.$toasted.global.sdr_app_error({
                        message: this.msgMissingBookingKey[this.getCurrentLocale],
                    });
                    return;
                }
                endpoint = '/booking/2fa/send';
                payload = {
                    bookingKeyCode: this.bookingKeyCode,
                    mobile: mobile,
                };
            }

            this.isSending = true;
            axios.put(endpoint, payload)
                .then((response) => {
                    const status = response.headers['air41-status'];
                    if (status === 'OK') {
                        this.pinSent = true;
                        this.phoneValue = formatGermanPhoneDisplay(this.phoneValue);
                        this.startResendCooldown();
                        this.focusPinInput();
                        return;
                    }

                    this.$toasted.global.sdr_app_error({
                        message: this.$trnsl_error_message(response.headers['air41-error']),
                    });
                })
                .catch((error) => {
                    this.$toasted.global.sdr_app_error({
                        message: 'Service unavailable: ' + error,
                    });
                })
                .finally(() => {
                    this.isSending = false;
                });
        },

        confirmPin: function () {
            if (!this.canConfirmPin) {
                this.$toasted.global.sdr_app_error({
                    message: this.msgNeedPin[this.getCurrentLocale],
                });
                return;
            }

            this.$emit('dd-2fa-confirmed', {
                phone: toGermanPhoneApi(this.phoneValue),
                twoFaSecret: this.pinValue,
            });
            this.closeModal();
        },
    },
};
</script>


<style>
.dd-2fa-modal-class {
    border-radius: 10px;
    background: #ffffff;
    border: 2px solid #A59D90 !important;
}

.dd-2fa-content {
    position: relative;
    padding: 28px 32px 32px;
}

.dd-2fa-content h2 {
    margin-bottom: 6px;
    padding-right: 32px;
}

.dd-2fa-subtitle {
    margin-top: 0;
    margin-bottom: 24px;
    font-size: 13px;
    line-height: 1.5;
    color: #5a5a5a;
}

.dd-2fa-close-btn {
    position: absolute;
    right: 14px;
    top: 14px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: none;
    font-size: 16px;
    color: #666;
}

.dd-2fa-send-row {
    margin: 14px 0 0 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
}

.dd-2fa-actions {
    margin-top: 28px;
    padding-top: 20px;
    border-top: 1px solid #ece8e0;
    display: flex;
    align-items: center;
    gap: 12px;
    justify-content: flex-end;
}

.dd-2fa-send-btn,
.dd-2fa-confirm-btn {
    height: auto !important;
    min-height: 40px;
    line-height: 1.25 !important;
    padding: 10px 22px !important;
    font-size: 1em;
    font-weight: bold;
}

a.dd-2fa-cancel-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 40px;
    padding: 10px 22px;
    background-color: #fff;
    color: #2d3e52;
    border: 2px solid #A59D90;
    border-radius: 6px;
    font-size: 1em;
    font-weight: bold;
    line-height: 1.25;
    cursor: pointer;
    transition: all 0.2s ease;
    text-decoration: none;
    box-sizing: border-box;
}

a.dd-2fa-cancel-btn:hover {
    background-color: #A59D90;
    color: #fff;
}

.dd-2fa-status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #3a7d1e;
    font-weight: 600;
    font-size: 13px;
}

.dd-2fa-step-card {
    position: relative;
    border: 1px solid #e0dbd2;
    border-radius: 8px;
    padding: 18px 20px;
    margin-bottom: 16px;
    background: #faf9f6;
    transition: border-color 0.2s, background 0.2s, opacity 0.2s;
}

.dd-2fa-step-card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
}

.dd-2fa-step-card__required {
    flex-shrink: 0;
    margin-top: 2px;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.2;
    color: #d6254d;
    white-space: nowrap;
}

.dd-2fa-step-card--active {
    border-color: #d35400;
    border-left: 4px solid #d35400;
    background: #fffaf5;
}

.dd-2fa-step-card--done {
    border-color: #b8ddb0;
    border-left: 4px solid #3a7d1e;
    background: #f6fbf4;
}

.dd-2fa-step-card--disabled {
    opacity: 0.55;
    pointer-events: none;
    background: #f3f3f3;
    border-color: #ddd;
    border-left: 4px solid #ccc;
}

.dd-2fa-step-title {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    font-weight: 700;
    margin-bottom: 0;
    color: #2d3e52;
    font-size: 15px;
    flex: 1;
    min-width: 0;
}

.dd-2fa-step-number {
    display: inline-flex;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    background: #2d3e52;
    color: #fff;
    flex-shrink: 0;
}

.dd-2fa-step-card--active .dd-2fa-step-number {
    background: #d35400;
}

.dd-2fa-step-card--done .dd-2fa-step-number {
    background: #3a7d1e;
}

.dd-2fa-step-card--disabled .dd-2fa-step-number {
    background: #aaa;
}

.dd-2fa-step-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 3px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.3px;
    text-transform: uppercase;
}

.dd-2fa-step-badge--active {
    background: #fde8e0;
    color: #c0392b;
}

.dd-2fa-step-badge--done {
    background: #e3f2de;
    color: #3a7d1e;
}

.dd-2fa-step-badge--locked {
    background: #e8e8e8;
    color: #777;
}

.dd-2fa-step-card .form-group {
    margin-bottom: 0;
}

.dd-2fa-step-card .form-group label {
    margin-bottom: 10px;
    font-size: 12px;
    color: #666;
}

.dd-2fa-phone-input {
    font-size: 15px;
    letter-spacing: 0.3px;
}

.dd-2fa-phone-hint {
    margin: 8px 0 0 0;
    font-size: 11px;
    line-height: 1.4;
    color: #888;
}

.dd-2fa-pin-group {
    margin-bottom: 0;
}

.dd-2fa-helper {
    margin-top: 10px;
    margin-bottom: 0;
    font-size: 12px;
    line-height: 1.45;
    color: #6b6b6b;
}

.dd-2fa-step-card .form-group + .form-group {
    margin-top: 12px;
}

.dd-2fa-pin-confirm {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-top: 14px;
    padding: 12px 14px;
    border: 1px solid #e0dbd2;
    border-radius: 6px;
    background: #fff;
    cursor: pointer;
    font-size: 13px;
    line-height: 1.45;
    color: #2d3e52;
    font-weight: 600;
}

.dd-2fa-pin-confirm input {
    margin-top: 2px;
    flex-shrink: 0;
    cursor: pointer;
}

.dd-2fa-helper--error {
    color: #c0392b;
}

.dd-2fa-helper--ok {
    color: #3a7d1e;
    font-weight: 600;
}

.dd-2fa-helper--ok i {
    color: #3a7d1e;
}

.dd-2fa-helper i {
    margin-right: 4px;
    color: #999;
}

button.dd-2fa-confirm-btn:disabled,
button.dd-2fa-send-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}
</style>
